import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {Plus, MapPin, Briefcase, Users, Pencil, Trash2, Play, Pause, XCircle, } from 'lucide-react'
import EmpresaLayout from '@/components/EmpresaLayout'
import VacancyFormModal from '@/components/VacancyFormModal'
import { useVacancies, type Vacancy } from '@/hooks/useVacancies'
import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'

const statusStyles: Record<string, string> = {
  publicada: 'bg-emerald-50 text-emerald-700',
  pausada: 'bg-amber-50 text-amber-700',
  cerrada: 'bg-slate-100 text-slate-500',
}

function EmpresaVacantes() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { vacancies, loading, fetchByCompany, createVacancy, updateVacancy, deleteVacancy } = useVacancies()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Vacancy | null>(null)
  const [companyName, setCompanyName] = useState('')

  const reload = useCallback(() => {
    if (user) fetchByCompany(user.userId)
  }, [user, fetchByCompany])

  useEffect(() => {
    if (!user) return
    reload()
    lumi.entities.companies
      .list({ filter: { userId: user.userId }, limit: 1 })
      .then((r) => {
        if (r.list.length > 0) setCompanyName(r.list[0].tradeName || '')
      })
      .catch((e) => console.error('Error al cargar empresa:', e))
  }, [user])

  const openCreate = () => {
    setEditing(null)
    setModalOpen(true)
  }

  const openEdit = (v: Vacancy) => {
    setEditing(v)
    setModalOpen(true)
  }

  const handleSubmit = async (data: Partial<Vacancy>) => {
    if (editing?._id) {
      await updateVacancy(editing._id, data)
    } else {
      await createVacancy({
        ...data,
        companyUserId: user!.userId,
        companyName: companyName || user?.email,
      })
    }
    reload()
    // Al guardar siempre se regresa al panel principal
    navigate('/empresa/dashboard')
  }

  const changeStatus = async (v: Vacancy, status: Vacancy['status']) => {
    await updateVacancy(v._id, { status })
    reload()
  }

  const handleDelete = async (v: Vacancy) => {
    if (!window.confirm(`¿Eliminar la vacante "${v.title}"? Esta acción no se puede deshacer.`)) return
    await deleteVacancy(v._id)
    reload()
  }

  const fmtSalary = (v: Vacancy) => {
    if (v.salaryMin && v.salaryMax) return `$${v.salaryMin.toLocaleString()} - $${v.salaryMax.toLocaleString()}`
    if (v.salaryMin) return `Desde $${v.salaryMin.toLocaleString()}`
    return 'No especificado'
  }

  return (
    <EmpresaLayout title="Vacantes" subtitle="Publica, edita, pausa y cierra tus oportunidades laborales">
      <div className="mb-5 flex justify-end">
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-amber-400 transition hover:bg-slate-800"
        >
          <Plus size={16} /> Publicar vacante
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20 text-sm text-slate-400">Cargando vacantes...</div>
      ) : vacancies.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
            <Briefcase size={22} />
          </div>
          <p className="mt-4 font-semibold text-slate-900">Aún no tienes vacantes</p>
          <p className="mt-1 text-sm text-slate-500">Publica tu primera oportunidad para atraer talento.</p>
          <button
            onClick={openCreate}
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-amber-300"
          >
            <Plus size={15} /> Publicar vacante
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {vacancies.map((v) => (
            <div key={v._id} className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-bold text-slate-900">{v.title}</h3>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${statusStyles[v.status]}`}>
                      {v.status}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm text-slate-500">{v.area || 'Sin área'} · {v.modality}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><MapPin size={13} /> {v.city || '—'}{v.state ? `, ${v.state}` : ''}</span>
                    <span className="inline-flex items-center gap-1"><Briefcase size={13} /> {fmtSalary(v)}</span>
                    <span className="inline-flex items-center gap-1"><Users size={13} /> {v.applicantsCount || 0} postulantes</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                <button onClick={() => openEdit(v)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
                  <Pencil size={13} /> Editar
                </button>
                {v.status !== 'publicada' && (
                  <button onClick={() => changeStatus(v, 'publicada')} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50">
                    <Play size={13} /> Publicar
                  </button>
                )}
                {v.status === 'publicada' && (
                  <button onClick={() => changeStatus(v, 'pausada')} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 px-3 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-50">
                    <Pause size={13} /> Pausar
                  </button>
                )}
                {v.status !== 'cerrada' && (
                  <button onClick={() => changeStatus(v, 'cerrada')} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-50">
                    <XCircle size={13} /> Cerrar
                  </button>
                )}
                <button onClick={() => handleDelete(v)} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">
                  <Trash2 size={13} /> Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <VacancyFormModal open={modalOpen} initial={editing} onClose={() => setModalOpen(false)} onSubmit={handleSubmit} />
    </EmpresaLayout>
  )
}

export default EmpresaVacantes
